import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  parseEnumFromString,
  parseStringFromEnum,
  TAB_QUERY_STRING,
} from '../../../utils/url-tabs-helper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TabPanel, { a11yProps } from '../../misc/TabsPanel';
import GeneralOrganizationSettings from '../settings/GeneralOrganizationSettings';
import OrganizationUserAccessSettings from '../settings/OrganizationUserAccessSettings';
import OrganizationApiKeySettings from '../settings/OrganizationApiKeySettings';
import OrganizationRole from '../../../models/enums/organization-roles';
import { useAppSelector } from '../../../store';
import { selectUser } from '../../../store/general/general-slice-selectors';
import { selectOrganizationsData } from '../../../store/organization/organization-slice-selectors';
import Typography from '@mui/material/Typography';

enum PageTabs {
  General,
  UserAccess,
  ApiKeys,
}

const OrganizationSettingsPage: React.FC = () => {
  const { t } = useTranslation();

  const user = useAppSelector((state) => selectUser(state));
  const organizationData = useAppSelector((state) => selectOrganizationsData(state));

  const [filterParams, setFilterParams] = useSearchParams();
  const pageTab = parseEnumFromString(PageTabs, filterParams.get(TAB_QUERY_STRING));

  const [tabsValue, setTabsValue] = useState(pageTab);

  const isExternalApiEnabled = organizationData.current?.isExternalApiEnabled;

  const tabChangeHandler = useCallback(
    (tab: PageTabs) => {
      setFilterParams((prevState) => {
        prevState.set(TAB_QUERY_STRING, parseStringFromEnum(PageTabs, tab));
        return prevState;
      });
      setTabsValue(tab);
    },
    [setFilterParams],
  );

  useEffect(() => {
    if (tabsValue !== pageTab) {
      tabChangeHandler(pageTab);
    }
  }, [pageTab, tabChangeHandler, tabsValue]);

  const tabChangeClickHandler = (_event: React.SyntheticEvent, newValue: number) => {
    tabChangeHandler(newValue);
  };

  return (
    <React.Fragment>
      <Stack spacing={2} p={2}>
        <Typography variant="h2" fontSize={24} lineHeight={2}>
          {`${organizationData.current?.name ?? ''} - ${t('button-settings')}`}
        </Typography>
        <Tabs
          value={tabsValue}
          onChange={tabChangeClickHandler}
          variant="scrollable"
          scrollButtons={false}
        >
          <Tab label={t('title-general-settings')} {...a11yProps(PageTabs.General)} />
          <Tab label={t('title-user-access-settings')} {...a11yProps(PageTabs.UserAccess)} />
          {(user.data.organization.role === OrganizationRole.MANUFACTURER ||
            isExternalApiEnabled) && (
            <Tab label={t('title-api-key-settings')} {...a11yProps(PageTabs.ApiKeys)} />
          )}
        </Tabs>

        <TabPanel value={tabsValue} index={PageTabs.General}>
          <GeneralOrganizationSettings />
        </TabPanel>
        <TabPanel value={tabsValue} index={PageTabs.UserAccess}>
          <OrganizationUserAccessSettings />
        </TabPanel>
        {(user.data.organization.role === OrganizationRole.MANUFACTURER ||
          isExternalApiEnabled) && (
          <TabPanel value={tabsValue} index={PageTabs.ApiKeys}>
            <OrganizationApiKeySettings />
          </TabPanel>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default OrganizationSettingsPage;
